import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  getServiceProviders, 
  getServiceProviderTypes,
  getSpecialties 
} from '../services/openapis';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Alert,
  Tooltip,
  Chip,
  Avatar,
  IconButton,
  Link,
  Button,
  Drawer,
  Divider,
  Pagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Paper,
} from '@mui/material';
import {
  LocalHospital as LocalHospitalIcon,
  LocationOn as LocationOnIcon,
  Business as BusinessIcon,
  Bed as BedIcon,
  Phone as PhoneIcon,
  ImageNotSupported as ImageNotSupportedIcon,
  Map as MapIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Sort as SortIcon,
} from '@mui/icons-material';

const BookmyopServiceProviders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State management
  const [providers, setProviders] = useState([]);
  const [providerTypes, setProviderTypes] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProviderType, setSelectedProviderType] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [sortBy, setSortBy] = useState('name');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(12);
  
  // UI states
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);

  // Sort options
  const sortOptions = [
    { value: 'name', label: 'Name (A-Z)' },
    { value: '-name', label: 'Name (Z-A)' },
    { value: 'bed_count', label: 'Beds (Low to High)' },
    { value: '-bed_count', label: 'Beds (High to Low)' },
    { value: 'created_at', label: 'Newest First' },
    { value: '-created_at', label: 'Oldest First' },
  ];

  // Update active filters display
  const updateActiveFilters = useCallback(() => {
    const filters = [];
    if (searchTerm) filters.push({ type: 'search', label: `Search: "${searchTerm}"` });
    if (selectedProviderType) {
      const providerType = providerTypes.find(pt => pt.id === selectedProviderType);
      if (providerType) filters.push({ type: 'providerType', label: `Type: ${providerType.name}` });
    }
    if (selectedSpecialty) {
      const specialty = specialties.find(s => s.id === selectedSpecialty);
      if (specialty) filters.push({ type: 'specialty', label: `Specialty: ${specialty.name}` });
    }
    setActiveFilters(filters);
  }, [searchTerm, selectedProviderType, selectedSpecialty, providerTypes, specialties]);

  // Clear specific filter
  const clearFilter = (filterType) => {
    switch (filterType) {
      case 'search':
        setSearchTerm('');
        break;
      case 'providerType':
        setSelectedProviderType('');
        break;
      case 'specialty':
        setSelectedSpecialty('');
        break;
      default:
        break;
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedProviderType('');
    setSelectedSpecialty('');
    setSortBy('name');
    setCurrentPage(1);
  };

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        
        // Fetch all data in parallel
        const [providerTypesData, specialtiesData] = await Promise.all([
          getServiceProviderTypes(),
          getSpecialties()
        ]);
        
        const providerTypesList = providerTypesData.results || [];
        const specialtiesList = specialtiesData.results || [];
        
        setProviderTypes(providerTypesList);
        setSpecialties(specialtiesList);
        
        // Check for URL parameters
        const searchParam = searchParams.get('search');
        const providerTypeParam = searchParams.get('providerType');
        const specialtyParam = searchParams.get('specialty');
        const sortParam = searchParams.get('sort');
        const pageParam = searchParams.get('page');
        
        // Handle URL parameters
        if (searchParam) setSearchTerm(searchParam);
        if (providerTypeParam) setSelectedProviderType(providerTypeParam);
        if (specialtyParam) setSelectedSpecialty(specialtyParam);
        if (sortParam) setSortBy(sortParam);
        if (pageParam) setCurrentPage(parseInt(pageParam));
        
        setError(null);
      } catch (err) {
        setError('Failed to fetch initial data. Please try again later.');
        console.error('Error fetching initial data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [searchParams]);

  // Fetch providers with filters and pagination
  useEffect(() => {
    // Skip if provider types not loaded yet
    if (providerTypes.length === 0) return;
    
    const fetchProviders = async () => {
      try {
        setLoading(true);
        
        const filters = {
          page: currentPage,
          pageSize: pageSize,
          ordering: sortBy,
        };
        
        if (searchTerm) filters.search = searchTerm;
        if (selectedSpecialty) filters.specialty = selectedSpecialty;
        
        const response = await getServiceProviders(filters);
        setProviders(response.results || []);
        setTotalPages(Math.ceil((response.count || 0) / pageSize));
        setTotalCount(response.count || 0);
        setError(null);
      } catch (err) {
        setError('Failed to fetch service providers. Please try again later.');
        console.error('Error fetching providers:', err);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchProviders, 500);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, selectedProviderType, selectedSpecialty, sortBy, currentPage, pageSize, providerTypes.length]);

  // Update active filters when filters change
  useEffect(() => {
    updateActiveFilters();
  }, [updateActiveFilters]);

  // Update URL parameters when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (searchTerm) params.set('search', searchTerm);
    if (selectedProviderType) params.set('providerType', selectedProviderType);
    if (selectedSpecialty) {
      const specialty = specialties.find(s => s.id === selectedSpecialty);
      if (specialty) params.set('specialty', specialty.name);
    }
    if (sortBy !== 'name') params.set('sort', sortBy);
    if (currentPage > 1) params.set('page', currentPage.toString());
    
    setSearchParams(params, { replace: true });
  }, [searchTerm, selectedProviderType, selectedSpecialty, sortBy, currentPage, specialties, setSearchParams]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getProviderTypeInfo = (provider) => {
    const providerType = providerTypes.find(type => type.id === provider.provider_type?.id);
    if (!providerType) return null;

    let info = providerType.description;
    if (providerType.bed_count_min !== null || providerType.bed_count_max !== null) {
      info += ` (${providerType.bed_count_min || 0} - ${providerType.bed_count_max || '∞'} beds)`;
    }
    return info;
  };

  // Left Sidebar Filters Component
  const LeftSidebarFilters = () => (
    <Paper elevation={1} sx={{ p: 3, height: 'fit-content', position: 'sticky', top: 20 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight="bold">
          Filters
        </Typography>
        <Button
          size="small"
          onClick={clearAllFilters}
          startIcon={<ClearIcon />}
          disabled={activeFilters.length === 0}
        >
          Clear All
        </Button>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      {/* Search */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight="bold" mb={1}>
          Search
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Search providers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Sort By */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight="bold" mb={1}>
          Sort By
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            {sortOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Provider Type */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight="bold" mb={1}>
          Provider Type
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={selectedProviderType}
            onChange={(e) => setSelectedProviderType(e.target.value)}
          >
            <MenuItem value="">All Types</MenuItem>
            {providerTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Specialty */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight="bold" mb={1}>
          Specialty
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={selectedSpecialty}
            onChange={(e) => setSelectedSpecialty(e.target.value)}
          >
            <MenuItem value="">All Specialties</MenuItem>
            {specialties.map((specialty) => (
              <MenuItem key={specialty.id} value={specialty.id}>
                {specialty.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Paper>
  );

  // Mobile Filter Drawer Content
  const FilterDrawerContent = () => (
    <Box sx={{ width: 280, p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Filters</Typography>
        <Button
          size="small"
          onClick={clearAllFilters}
          startIcon={<ClearIcon />}
        >
          Clear All
        </Button>
      </Box>
      
      <Divider sx={{ mb: 2 }} />
      
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Sort By</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth size="small">
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {sortOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Provider Type</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth size="small">
            <Select
              value={selectedProviderType}
              onChange={(e) => setSelectedProviderType(e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              {providerTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Specialty</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth size="small">
            <Select
              value={selectedSpecialty}
              onChange={(e) => setSelectedSpecialty(e.target.value)}
            >
              <MenuItem value="">All Specialties</MenuItem>
              {specialties.map((specialty) => (
                <MenuItem key={specialty.id} value={specialty.id}>
                  {specialty.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  if (loading && providers.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Service Providers
        </Typography>
        {isMobile && (
          <IconButton
            onClick={() => setFilterDrawerOpen(true)}
            color="primary"
            sx={{ border: 1, borderColor: 'divider' }}
          >
            <FilterIcon />
          </IconButton>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Sidebar - Filters (Desktop) */}
        {!isMobile && (
          <Grid item xs={12} md={3}>
            <LeftSidebarFilters />
          </Grid>
        )}

        {/* Right Side - Results */}
        <Grid item xs={12} md={9}>
          {/* Active Filters Summary */}
          {activeFilters.length > 0 && (
            <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Applied Filters:
                </Typography>
                <Button
                  size="small"
                  onClick={clearAllFilters}
                  startIcon={<ClearIcon />}
                >
                  Clear All
                </Button>
              </Box>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {activeFilters.map((filter, index) => (
                  <Chip
                    key={index}
                    label={filter.label}
                    onDelete={() => clearFilter(filter.type)}
                    size="small"
                    color="primary"
                    variant="outlined"
                    deleteIcon={<CloseIcon />}
                  />
                ))}
              </Box>
            </Paper>
          )}

          {/* Results Summary */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="body2" color="textSecondary">
              {totalCount > 0 ? `${totalCount} provider${totalCount !== 1 ? 's' : ''} found` : 'No providers found'}
            </Typography>
          </Box>

          {/* Providers Grid */}
          <Grid container spacing={3}>
            {providers.length === 0 ? (
              <Grid item xs={12}>
                <Box textAlign="center" py={4}>
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    No service providers found
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Try adjusting your search criteria or filters
                  </Typography>
                </Box>
              </Grid>
            ) : (
              providers.map((provider) => (
                <Grid item xs={12} sm={6} lg={4} key={provider.id}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      {/* Provider Name with Image */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Avatar
                          src={provider.building_image}
                          alt={provider.name}
                          variant="rounded"
                          sx={{
                            width: 64,
                            height: 64,
                            mr: 2,
                            bgcolor: 'action.hover'
                          }}
                        >
                          <ImageNotSupportedIcon />
                        </Avatar>
                        <Box flex={1}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <LocalHospitalIcon sx={{ mr: 1, color: 'primary.main', fontSize: 'small' }} />
                            <Typography variant="h6" component="h2" noWrap>
                              {provider.name}
                            </Typography>
                          </Box>
                          {/* Provider Type */}
                          <Tooltip title={getProviderTypeInfo(provider) || ''}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <BusinessIcon sx={{ mr: 1, fontSize: 'small', color: 'text.secondary' }} />
                              <Typography color="textSecondary" variant="body2" noWrap>
                                {provider.provider_type_name}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* Address and Location */}
                      <Box sx={{ mb: 2 }}>
                        {provider.address && (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                            <LocationOnIcon sx={{ mr: 1.5, color: 'text.secondary' }} />
                            <Typography variant="body2" color="textSecondary">
                              {provider.address}
                            </Typography>
                          </Box>
                        )}
                        {provider.location && (
                          <Box sx={{ display: 'flex', alignItems: 'left', ml: 0.5 }}>
                            <Link
                              href={provider.location}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: 'primary.main',
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                            >
                              <MapIcon sx={{ fontSize: 'large', mr: 2.5 }} />
                              <Typography variant="body2">
                                View on Maps
                              </Typography>
                            </Link>
                          </Box>
                        )}
                      </Box>

                      {/* Contact */}
                      {provider.coordinator_contact && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <PhoneIcon sx={{ mr: 1, fontSize: 'small', color: 'text.secondary' }} />
                          <Typography variant="body2" color="textSecondary">
                            {provider.coordinator_contact}
                          </Typography>
                        </Box>
                      )}

                      {/* Additional Info */}
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        {provider.bed_count > 0 && (
                          <Tooltip title="Number of beds">
                            <Chip
                              icon={<BedIcon />}
                              label={`${provider.bed_count} Beds`}
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                        <Chip
                          label={provider.status}
                          size="small"
                          color={provider.status === 'approved' ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? "small" : "medium"}
              />
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Mobile Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
      >
        <FilterDrawerContent />
      </Drawer>
    </Container>
  );
};

export default BookmyopServiceProviders;
